import React, { useState, useEffect, useRef } from "react";
import {
  Box, Card, CardContent, Typography, Chip,
  TextField, MenuItem, Button, Divider,
  CircularProgress, Grid, Avatar,
} from "@mui/material";
import {
  ArrowLeft, Plus, Mail, Phone, Globe, Tag, Users,
  FileText, MessageSquare, PhoneCall, RefreshCw, CheckCircle2, StickyNote,
} from "lucide-react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import AdminShell from "../layout/AdminShell";
import {
  adminGetLead, adminUpdateLead,
  getFollowUps, createFollowUp,
  getActivities, createActivity,
} from "../../api/leads";
import { adminGetUsers } from "../../api/users";

const STATUS_OPTIONS = ["NEW","CONTACTED","FOLLOW_UP","QUALIFIED","CONVERTED","LOST"];
const PRIORITY_OPTIONS = ["HOT","WARM","COLD"];
const FOLLOW_UP_TYPES = ["CALL","EMAIL","MEETING","OTHER"];

const STATUS_COLORS = {
  NEW: "default", CONTACTED: "primary", FOLLOW_UP: "warning",
  QUALIFIED: "info", CONVERTED: "success", LOST: "error",
};
const PRIORITY_COLORS = { HOT: "error", WARM: "warning", COLD: "info" };

const ACTIVITY_META = {
  NOTE:          { icon: StickyNote,  color: "#64748B" },
  STATUS_CHANGE: { icon: RefreshCw,   color: "#2563EB" },
  CALL:          { icon: PhoneCall,   color: "#22C55E" },
  EMAIL:         { icon: Mail,        color: "#F59E0B" },
  MEETING:       { icon: Users,       color: "#8B5CF6" },
};

const fieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: 13.5, bgcolor: "#F8FAFF" },
};

function InfoRow({ icon: Icon, label, value, link }) {
  if (!value) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, py: 0.85 }}>
      <Icon size={15} color="#94A3B8" style={{ marginTop: 2, flexShrink: 0 }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 11.5, color: "text.secondary", lineHeight: 1.3 }}>{label}</Typography>
        {link ? (
          <Typography
            component="a" href={link} target="_blank" rel="noreferrer"
            sx={{ fontSize: 13.5, color: "#2563EB", fontWeight: 600, wordBreak: "break-word", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
          >
            {value}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "#0F172A", wordBreak: "break-word" }}>{value}</Typography>
        )}
      </Box>
    </Box>
  );
}

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const [note, setNote] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const followUpCardRef = useRef(null);
  const followUpDateRef = useRef(null);

  const { data: lead, isLoading } = useQuery({
    queryKey: ["lead", id],
    queryFn: () => adminGetLead(id).then(r => r.data),
  });

  const { data: followUps = [] } = useQuery({
    queryKey: ["follow-ups", id],
    queryFn: () => getFollowUps(id).then(r => r.data),
  });

  const { data: activities = [] } = useQuery({
    queryKey: ["activities", id],
    queryFn: () => getActivities(id).then(r => r.data),
  });

  const { data: users = [] } = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => adminGetUsers().then(r => r.data),
  });

  const { register: regFU, handleSubmit: handleFU, reset: resetFU } = useForm();

  useEffect(() => {
    if (lead) setInternalNotes(lead.notes || "");
  }, [lead]);

  // Arrived here via the "Follow up" button on the Leads list — jump straight
  // to the add-follow-up form.
  useEffect(() => {
    if (searchParams.get("action") === "follow-up" && followUpCardRef.current) {
      followUpCardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      followUpDateRef.current?.focus();
    }
  }, [searchParams, lead]);

  // The PUT endpoint validates the full payload (confirmed by the 400 errors
  // in the API docs for missing/placeholder fields), so partial updates risk
  // silently blanking out the rest of the record. Always send everything,
  // merged with just the field(s) actually being changed.
  const buildFullPayload = (overrides) => ({
    name: lead.name,
    email: lead.email,
    phone: lead.phone,
    visitor_type: lead.visitor_type,
    enquiry_type: lead.enquiry_type,
    requirement_text: lead.requirement_text,
    source: lead.source,
    source_url: lead.source_url || "",
    utm_source: lead.utm_source || "",
    utm_medium: lead.utm_medium || "",
    utm_campaign: lead.utm_campaign || "",
    status: lead.status,
    priority: lead.priority,
    assigned_to: lead.assigned_to,
    notes: lead.notes || "",
    ...overrides,
  });

  const updateMutation = useMutation({
    mutationFn: (data) => adminUpdateLead(id, data),
    onSuccess: () => { qc.invalidateQueries(["lead", id]); qc.invalidateQueries(["activities", id]); toast.success("Lead updated"); },
    onError: (err) => toast.error(err?.response?.data ? JSON.stringify(err.response.data) : "Update failed"),
  });

  const followUpMutation = useMutation({
    mutationFn: (data) => createFollowUp(id, data),
    onSuccess: () => {
      qc.invalidateQueries(["follow-ups", id]);
      qc.invalidateQueries(["activities", id]);
      resetFU();
      toast.success("Follow-up added");
    },
    onError: () => toast.error("Failed to add follow-up"),
  });

  const noteMutation = useMutation({
    mutationFn: () => createActivity(id, { type: "NOTE", details: { note } }),
    onSuccess: () => { qc.invalidateQueries(["activities", id]); setNote(""); toast.success("Note added"); },
    onError: () => toast.error("Failed to add note"),
  });

  const notesFieldMutation = useMutation({
    mutationFn: () => adminUpdateLead(id, buildFullPayload({ notes: internalNotes })),
    onSuccess: () => { qc.invalidateQueries(["lead", id]); toast.success("Internal notes saved"); },
    onError: () => toast.error("Couldn't save notes"),
  });

  if (isLoading) return (
    <AdminShell title="Lead Detail">
      <Box display="flex" justifyContent="center" mt={10}><CircularProgress /></Box>
    </AdminShell>
  );

  const initials = (lead?.name || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <AdminShell title={lead?.name ?? "Lead Detail"}>
      <Button
        startIcon={<ArrowLeft size={16} />}
        onClick={() => navigate("/admin/leads")}
        sx={{ mb: 2, textTransform: "none", fontWeight: 600, color: "text.secondary" }}
      >
        Back to Leads
      </Button>

      {/* Header card */}
      <Card sx={{ mb: 2.5, borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar sx={{ width: 52, height: 52, bgcolor: "rgba(37,99,235,0.1)", color: "#2563EB", fontWeight: 700, fontSize: 18 }}>
                {initials}
              </Avatar>
              <Box>
                <Typography sx={{ fontSize: 19, fontWeight: 700, color: "#0F172A" }}>{lead.name}</Typography>
                <Box sx={{ display: "flex", gap: 0.75, mt: 0.5, flexWrap: "wrap" }}>
                  <Chip label={lead.status_display || lead.status} color={STATUS_COLORS[lead.status] || "default"} size="small" />
                  {lead.priority && <Chip label={lead.priority_display || lead.priority} color={PRIORITY_COLORS[lead.priority] || "default"} size="small" />}
                  <Chip label={lead.visitor_type_display || lead.visitor_type} size="small" variant="outlined" />
                  {lead.enquiry_type && <Chip label={lead.enquiry_type} size="small" variant="outlined" sx={{ textTransform: "capitalize" }} />}
                </Box>
              </Box>
            </Box>
            <Button
              variant="contained" size="small"
              startIcon={<PhoneCall size={15} />}
              onClick={() => followUpCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", bgcolor: "#2563EB", "&:hover": { bgcolor: "#1D4ED8" } }}
            >
              Add Follow-up
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={2.5}>
        {/* LEFT column */}
        <Grid item xs={12} lg={4}>
          {/* Contact & source info */}
          <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F172A", mb: 0.5 }}>Contact Info</Typography>
              <InfoRow icon={Mail} label="Email" value={lead.email} link={`mailto:${lead.email}`} />
              <InfoRow icon={Phone} label="Phone" value={lead.phone} link={`tel:${lead.phone}`} />
              <Divider sx={{ my: 1 }} />
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F172A", mb: 0.5, mt: 1 }}>Source</Typography>
              <InfoRow icon={Tag} label="Source" value={lead.source} />
              <InfoRow icon={Globe} label="Source URL" value={lead.source_url} link={lead.source_url || undefined} />
              {(lead.utm_source || lead.utm_medium || lead.utm_campaign) && (
                <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap", mt: 1 }}>
                  {lead.utm_source && <Chip label={`utm_source: ${lead.utm_source}`} size="small" variant="outlined" />}
                  {lead.utm_medium && <Chip label={`utm_medium: ${lead.utm_medium}`} size="small" variant="outlined" />}
                  {lead.utm_campaign && <Chip label={`utm_campaign: ${lead.utm_campaign}`} size="small" variant="outlined" />}
                </Box>
              )}
              <Divider sx={{ my: 1.5 }} />
              <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                Created {dayjs(lead.created_at).format("DD MMM YYYY, h:mm A")}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                Updated {dayjs(lead.updated_at).format("DD MMM YYYY, h:mm A")}
              </Typography>
            </CardContent>
          </Card>

          {/* Requirement */}
          <Card sx={{ mt: 2, borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
                <FileText size={15} color="#94A3B8" />
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>Requirement</Typography>
              </Box>
              <Typography sx={{ fontSize: 13.5, color: "#334155", lineHeight: 1.6 }}>
                {lead.requirement_text || "—"}
              </Typography>
            </CardContent>
          </Card>

          {/* Quick update */}
          <Card sx={{ mt: 2, borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F172A", mb: 1.5 }}>Manage Lead</Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <TextField
                  select size="small" label="Status" sx={fieldSx}
                  defaultValue={lead.status}
                  onChange={(e) => updateMutation.mutate(buildFullPayload({ status: e.target.value }))}
                >
                  {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s.replace("_", " ")}</MenuItem>)}
                </TextField>
                <TextField
                  select size="small" label="Priority" sx={fieldSx}
                  defaultValue={lead.priority || ""}
                  onChange={(e) => updateMutation.mutate(buildFullPayload({ priority: e.target.value }))}
                >
                  {PRIORITY_OPTIONS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </TextField>
                <TextField
                  select size="small" label="Assign To" sx={fieldSx}
                  defaultValue={lead.assigned_to ?? ""}
                  onChange={(e) => updateMutation.mutate(buildFullPayload({ assigned_to: e.target.value || null }))}
                >
                  <MenuItem value="">Unassigned</MenuItem>
                  {users.map(u => <MenuItem key={u.id} value={u.id}>{u.full_name}</MenuItem>)}
                </TextField>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F172A", mb: 1 }}>Internal Notes</Typography>
              <TextField
                fullWidth multiline rows={3} size="small" sx={fieldSx}
                placeholder="Notes visible only to the team..."
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
              />
              <Button
                size="small" variant="outlined" sx={{ mt: 1, textTransform: "none", fontWeight: 600, borderRadius: "8px" }}
                disabled={internalNotes === (lead.notes || "") || notesFieldMutation.isPending}
                onClick={() => notesFieldMutation.mutate()}
              >
                Save Notes
              </Button>
            </CardContent>
          </Card>

          {/* Add Follow-up */}
          <Card ref={followUpCardRef} sx={{ mt: 2, borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
                <Plus size={15} color="#94A3B8" />
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>Add Follow-up</Typography>
              </Box>
              <Box component="form" onSubmit={handleFU(d => followUpMutation.mutate(d))} sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <TextField
                  size="small" type="date" label="Date" sx={fieldSx}
                  inputRef={followUpDateRef}
                  {...regFU("follow_up_date", { required: true })}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField select size="small" label="Type" defaultValue="CALL" sx={fieldSx} {...regFU("follow_up_type")}>
                  {FOLLOW_UP_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
                <TextField size="small" label="Notes" multiline rows={2} sx={fieldSx} {...regFU("notes")} />
                <Button
                  type="submit" variant="contained" size="small" disabled={followUpMutation.isPending}
                  sx={{ textTransform: "none", fontWeight: 700, borderRadius: "8px", bgcolor: "#2563EB", "&:hover": { bgcolor: "#1D4ED8" } }}
                >
                  {followUpMutation.isPending ? "Adding..." : "Add Follow-up"}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* RIGHT column */}
        <Grid item xs={12} lg={8}>
          {/* Add Note */}
          <Card sx={{ mb: 2, borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
                <MessageSquare size={15} color="#94A3B8" />
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>Add Activity Note</Typography>
              </Box>
              <TextField
                fullWidth multiline rows={2} size="small" sx={fieldSx}
                placeholder="Write a note about this lead..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <Button
                variant="outlined" size="small" sx={{ mt: 1.25, textTransform: "none", fontWeight: 600, borderRadius: "8px" }}
                disabled={!note.trim() || noteMutation.isPending}
                onClick={() => noteMutation.mutate()}
              >
                Save Note
              </Button>
            </CardContent>
          </Card>

          {/* Follow-ups */}
          {followUps.length > 0 && (
            <Card sx={{ mb: 2, borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
              <CardContent sx={{ p: 2.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F172A", mb: 1.5 }}>
                  Scheduled Follow-ups
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {followUps.map((fu) => (
                    <Box
                      key={fu.id}
                      sx={{
                        p: 1.5, borderRadius: "12px",
                        bgcolor: fu.completed ? "rgba(34,197,94,0.05)" : "#F8FAFC",
                        border: "1px solid",
                        borderColor: fu.completed ? "rgba(34,197,94,0.2)" : "rgba(15,23,42,0.06)",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Chip label={fu.follow_up_type} size="small" />
                          {fu.completed && <CheckCircle2 size={15} color="#22C55E" />}
                          {fu.outcome_display && <Chip label={fu.outcome_display} size="small" variant="outlined" />}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {dayjs(fu.follow_up_date).format("DD MMM YYYY")}
                          {fu.follow_up_time ? ` · ${dayjs(`2000-01-01T${fu.follow_up_time}`).format("h:mm A")}` : ""}
                        </Typography>
                      </Box>
                      {fu.notes && <Typography variant="body2" mt={1} sx={{ fontSize: 13.5, color: "#334155" }}>{fu.notes}</Typography>}
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Activity timeline */}
          <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F172A", mb: 2 }}>Activity Timeline</Typography>
              {activities.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No activity yet</Typography>
              ) : (
                <Box sx={{ position: "relative" }}>
                  {activities.map((a, i) => {
                    const meta = ACTIVITY_META[a.type] || ACTIVITY_META.NOTE;
                    const Icon = meta.icon;
                    const isLast = i === activities.length - 1;
                    return (
                      <Box key={a.id} sx={{ display: "flex", gap: 1.75, pb: isLast ? 0 : 2.5, position: "relative" }}>
                        {!isLast && (
                          <Box sx={{ position: "absolute", left: 15, top: 32, bottom: -4, width: "1px", bgcolor: "rgba(15,23,42,0.08)" }} />
                        )}
                        <Box sx={{
                          width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                          bgcolor: `${meta.color}18`, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1,
                        }}>
                          <Icon size={15} color={meta.color} />
                        </Box>
                        <Box sx={{ pt: 0.4 }}>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#0F172A" }}>
                            {a.type_display || a.type}
                            {a.created_by_name && <Typography component="span" sx={{ fontSize: 12, color: "text.secondary", fontWeight: 500 }}> · {a.created_by_name}</Typography>}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {dayjs(a.created_at).format("DD MMM YYYY, h:mm A")}
                          </Typography>
                          {a.details?.note && (
                            <Typography variant="body2" mt={0.5} sx={{ fontSize: 13.5, color: "#334155" }}>{a.details.note}</Typography>
                          )}
                          {a.type === "STATUS_CHANGE" && a.details?.from && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5 }}>
                              <Chip label={a.details.from} size="small" variant="outlined" />
                              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>→</Typography>
                              <Chip label={a.details.to} size="small" color={STATUS_COLORS[a.details.to] || "default"} />
                            </Box>
                          )}
                          {typeof a.details === "string" && a.details && a.type !== "STATUS_CHANGE" && (
                            <Typography variant="body2" mt={0.5} sx={{ fontSize: 13.5, color: "#334155" }}>{a.details}</Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </AdminShell>
  );
}